import React, { memo, useState } from 'react'
import classNames from 'classnames'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { FilterCarrier } from 'apps/fleet-planner/pages/loads/FilterCarrier'
import { FilterCustomer } from 'apps/fleet-planner/pages/loads/FilterCustomer'
import { FilterDispatcher } from 'apps/fleet-planner/pages/loads/FilterDispatcher'
import { FilterDriver } from 'apps/fleet-planner/pages/loads/FilterDriver'
import { FilterDropoffDate } from 'apps/fleet-planner/pages/loads/FilterDropoffDate'
import {
  FilterFacilityDropoffLocation,
  FilterFacilityLocationPickup,
} from 'apps/fleet-planner/pages/loads/FilterFacilityLocation'
import { FilterMaxAge } from 'apps/fleet-planner/pages/loads/FilterMaxAge'
import { FilterPickupDate } from 'apps/fleet-planner/pages/loads/FilterPickupDate'
import { FilterReferenceNumber } from 'apps/fleet-planner/pages/loads/FilterReferenceNumber'
import { LoadsFiltersModal } from 'apps/fleet-planner/pages/loads/LoadsFiltersModal'
import 'apps/fleet-planner/scss/components/_loads-filters.scss'
import { ILoadFilters, SORT_FIELD, SORT_ORDER } from 'apps/fleet-planner/types'
import { getFilterCount } from 'apps/fleet-planner/utils/getFilterCount'
import { FilterIcon } from 'transfix-icons'
import { Button, Facet } from 'transfix-ui'
import { LoadsSortingOptions } from './LoadsSortingOptions'

interface ILoadFiltersProps {
  currentFilters: Partial<ILoadFilters>
  onFilterChange: (newFilters: Partial<ILoadFilters>) => void
  onClearFilter: () => void
  // onSortChange is only used for mobile views
  onSortChange?: (sortingOption: SORT_FIELD, sortingOrder?: SORT_ORDER) => void
}

export const LoadsFilters = memo<ILoadFiltersProps>(
  ({ currentFilters, onClearFilter, onFilterChange, onSortChange }) => {
    const { isMobile } = useBreakpointContext()
    const showShipmentMaxAgeFilter = useFeatureFlag('fmsShipmentMaxAgeFilter')
    const filterCount = getFilterCount(currentFilters)

    const [isFilterModalOpen, setFilterModalOpen] = useState<boolean>(false)

    const filterCountBadge =
      filterCount > 0 ? <span className='tag tag-green mr1'>{filterCount}</span> : ''

    const toggleModalHandler = () => setFilterModalOpen(prev => !prev)
    const closeModalHandler = () => setFilterModalOpen(false)

    const facetClasses = classNames({
      applied: filterCount > 0,
    })

    const onChangeHandler = (sortingOption: SORT_FIELD) => {
      if (onSortChange) onSortChange(sortingOption, SORT_ORDER.DESC)
    }

    return (
      <div className='loads-filter-container'>
        {!!isMobile && isFilterModalOpen && (
          <LoadsFiltersModal
            currentFilters={currentFilters}
            isFilterModalOpen={isFilterModalOpen}
            onCloseModal={closeModalHandler}
            onChangeFilter={onFilterChange}
            onClearFilter={onClearFilter}
          />
        )}
        {!isMobile && (
          <div className='order-0'>
            <div className='col-xs-12 col-md-4 col-lg-3 mb3 mb0-m'>
              <FilterFacilityLocationPickup
                value={currentFilters}
                onChange={onFilterChange}
                label='Pickup Location'
                placeholder='Enter a city, state, or facility'
                dataTest='loads-filter-pu-loc'
              />
            </div>
            <div className='col-xs-12 col-md-4 col-lg-3 mb3 mb0-m'>
              <FilterFacilityDropoffLocation
                value={currentFilters}
                onChange={onFilterChange}
                label='Dropoff Location'
                placeholder='Enter a city, state, or facility'
                dataTest='loads-filter-do-loc'
              />
            </div>
            <div className='col-xs-12 ph0 col-md-8 col-lg-3 mb3 mb0-m'>
              <div className='col-xs-6'>
                <FilterPickupDate value={currentFilters} onChange={onFilterChange} />
              </div>
              <div className='col-xs-6'>
                <FilterDropoffDate value={currentFilters} onChange={onFilterChange} />
              </div>
            </div>
            <div className='col-xs-12 col-md-4 col-lg-2'>
              <FilterReferenceNumber value={currentFilters} onChange={onFilterChange} />
            </div>
            <div className='col-xs-12 col-md-4 col-lg-1'>
              <Button
                color='tertiary'
                size='large'
                onClick={onClearFilter}
                className='loads-filters-clear-all-btn'
                data-test='loads-filters-clear-all-btn'>
                Clear All
              </Button>
            </div>
          </div>
        )}
        <div className='loads-filter-row'>
          {isMobile && (
            <>
              <div className='mr2 mr3-m'>
                <Facet
                  selected={isFilterModalOpen}
                  id='toggle-filter-modal'
                  dataTest='toggle-filter-modal'
                  icon={<FilterIcon size='large' />}
                  label='Filters'
                  onClick={toggleModalHandler}
                  badge={filterCountBadge}
                  buttonClassName={facetClasses}
                />
              </div>
              <div className='mr2 mr3-m'>
                <LoadsSortingOptions onChange={onChangeHandler} />
              </div>
            </>
          )}
          <div className='mr2 mr3-m'>
            <FilterDriver value={currentFilters} onChange={onFilterChange} />
          </div>
          <div className='mr2 mr3-m'>
            <FilterDispatcher value={currentFilters} onChange={onFilterChange} />
          </div>
          <div className='pr3'>
            <FilterCustomer value={currentFilters} onChange={onFilterChange} />
          </div>
          <div className='mr2 mr3-m'>
            <FilterCarrier value={currentFilters} onChange={onFilterChange} />
          </div>
          {showShipmentMaxAgeFilter && (
            <div className='mr2 mr3-m'>
              <FilterMaxAge value={currentFilters} onChange={onFilterChange} />
            </div>
          )}
        </div>
      </div>
    )
  }
)
